import { db, storage } from '../firebase';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, collection, query, where, getDocs, addDoc, deleteDoc, onSnapshot, serverTimestamp, limit, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * Creates or updates a user profile in Firestore.
 * Warning: Merges data, does not overwrite existing wallet/stats unless specified.
 */
export const createUserProfile = async (user) => {
    if (!user || !user.uid) return null;

    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    const defaultData = {
        ...user, // Spread all passed fields (gender, whatsapp, dob, etc.)
        uid: user.uid,
        name: user.displayName || user.name || 'Anonymous',
        email: user.email,
        photoURL: user.photoURL || user.images?.[0] || null,
        joinedAt: new Date().toISOString(),
        wallet: user.wallet || { coins: 0 },
        stats: user.stats || {
            visits: 0,
            followers: 0,
            coinsEarned: 0,
            followers24h: 0, // Mock: In real app, calculate from sub-collection
            coins24h: 0      // Mock
        },
        images: user.images || [] // Preserve existing images array structure if passed
    };

    if (!userSnap.exists()) {
        await setDoc(userRef, defaultData);
        return defaultData;
    } else {
        // Update basic info but preserve stats/wallet
        const currentData = userSnap.data();
        // Only update if missing (or specific fields if needed)
        // For now, return the full profile
        return currentData;
    }
};

/**
 * Fetches full user profile data.
 */
export const getUserData = async (uid) => {
    if (!uid) return null;
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
        return userSnap.data();
    }
    return null;
};

/**
 * Updates wallet balance.
 * operation: 'add' or 'subtract'
 */
export const updateWallet = async (uid, amount, operation = 'add') => {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) return null;

    const currentCoins = userSnap.data().wallet?.coins || 0;
    const newBalance = operation === 'add'
        ? currentCoins + parseFloat(amount)
        : currentCoins - parseFloat(amount);

    if (newBalance < 0) throw new Error('Insufficient funds');

    await updateDoc(userRef, {
        'wallet.coins': newBalance
    });

    return newBalance;
};

/**
 * Uploads media (photo/video) and adds it to the user's profile media array (if we had one).
 * For now, we will assume media is just part of the 'images' array or a new 'media' collection.
 * This function returns the URL.
 */
export const uploadUserMedia = async (uid, file, type = 'photos') => {
    const storageRef = ref(storage, `users/${uid}/${type}/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    // Add to Firestore array
    const userRef = doc(db, 'users', uid);
    // Determine field based on type. Note: ProfileDashboard expects 'photos' or 'videos' arrays if we align with it.
    // Let's create a generic 'media' array with type field.
    const mediaItem = {
        id: Date.now(),
        src: url,
        type: type, // 'photo' or 'video'
        date: new Date().toISOString().split('T')[0]
    };

    await updateDoc(userRef, {
        media: arrayUnion(mediaItem)
    });

    return mediaItem;
};

/**
 * Updates user online status and location.
 */
export const updateUserStatus = async (uid, isOnline, location = null, userData = null) => {
    if (!uid) return;
    const userRef = doc(db, 'users', uid);

    let updateData = {
        isOnline: isOnline,
        lastActive: new Date().toISOString()
    };

    if (location) {
        updateData.location = location; // { lat, lng }
    }

    // Merge basic profile data if provided (prevents ghost users)
    // Merge basic profile data if provided (prevents ghost users)
    if (userData) {
        // Always update these fields if provided to ensure they aren't "Anonymous"
        if (userData.name) updateData.name = userData.name;
        if (userData.photoURL) updateData.photoURL = userData.photoURL;
        if (userData.images && userData.images.length > 0) updateData.images = userData.images;
        if (userData.gender) updateData.gender = userData.gender;
        if (userData.interestedIn) updateData.interestedIn = userData.interestedIn;
        if (userData.city) updateData.city = userData.city;
    }

    try {
        // Use setDoc with merge to ensure document exists
        await setDoc(userRef, updateData, { merge: true });
    } catch (error) {
        console.error("Error updating status:", error);
    }
};


/**
 * Updates specific fields in a user's profile.
 */
export const updateUserProfile = async (uid, data) => {
    if (!uid) return;
    const userRef = doc(db, 'users', uid);
    try {
        await setDoc(userRef, data, { merge: true });
        console.log("Profile updated successfully");
    } catch (error) {
        console.error("Error updating profile:", error);
        throw error;
    }
};

export const seedTestUser = async () => {
    try {
        const seedId = 'seed_' + Date.now();
        const userRef = doc(db, 'users', seedId);

        // Create a timeout promise
        const timeout = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Write timed out involved (Firewall/Network?)")), 5000)
        );

        // Race setDoc against timeout
        await Promise.race([
            setDoc(userRef, {
                uid: seedId,
                name: 'Test Seed ' + Math.floor(Math.random() * 1000),
                gender: 'female',
                isOnline: true,
                joinedAt: new Date().toISOString(),
                images: ['https://api.dicebear.com/7.x/avataaars/svg?seed=' + seedId],
                wallet: { coins: 100 },
                stats: { visits: 0, followers: 0 }
            }),
            timeout
        ]);

        return { success: true, uid: seedId };
    } catch (error) {
        console.error("Seed error:", error);
        return { success: false, error: error.message };
    }
};

/**
 * Fetches multiple user profiles by UID in a single batch.
 */
export const getUsersBatch = async (uids) => {
    if (!uids || uids.length === 0) return [];
    try {
        const usersRef = collection(db, 'users');
        // Firestore 'in' query supports up to 30 IDs per batch
        const uidsToFetch = uids.slice(0, 30);
        const q = query(usersRef, where('uid', 'in', uidsToFetch));
        const querySnapshot = await getDocs(q);

        const users = [];
        querySnapshot.forEach((doc) => {
            users.push({ id: doc.id, ...doc.data() });
        });
        return users;
    } catch (error) {
        console.error("Error fetching users batch:", error);
        return [];
    }
};

/**
 * Fetches all registered users (for Home Feed).
 */
export const getAllUsers = async () => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, orderBy("joinedAt", "desc"), limit(100));
        const querySnapshot = await getDocs(q);

        const users = [];
        querySnapshot.forEach((doc) => {
            users.push({ id: doc.id, ...doc.data() });
        });

        // Sort in memory by joinedAt if available, otherwise fallback
        users.sort((a, b) => {
            const dateA = a.joinedAt ? new Date(a.joinedAt) : new Date(0);
            const dateB = b.joinedAt ? new Date(b.joinedAt) : new Date(0);
            return dateB - dateA; // Descending
        });

        return users;
    } catch (error) {
        console.error("Error fetching all users:", error);
        return [];
    }
};

/**
 * Fetches online users sorted by distance (if location provided).
 */
export const getOnlineUsers = async (currentLocation = null) => {
    try {
        const usersRef = collection(db, 'users');
        // Simple query: get all users marked as isOnline = true
        // For scalability, we'd need better indexing or limit()
        const q = query(usersRef, where("isOnline", "==", true), limit(50));
        const querySnapshot = await getDocs(q);

        const users = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            // Don't include self if needed, but easier to filter in UI
            // Calculate distance if we have both locations
            let distance = Infinity;

            if (currentLocation && data.location) {
                distance = calculateDistance(
                    currentLocation.lat, currentLocation.lng,
                    data.location.lat, data.location.lng
                );
            }

            // Format for UI
            users.push({
                ...data,
                id: doc.id,
                distance: distance
            });
        });

        // Sort by distance
        if (currentLocation) {
            users.sort((a, b) => a.distance - b.distance);
        } else {
            // Sort by recently active if no location
            users.sort((a, b) => new Date(b.lastActive) - new Date(a.lastActive));
        }

        return users;
    } catch (error) {
        console.error("Error fetching online users:", error);
        return [];
    }
};

/**
 * Listens to online users in real-time.
 */
export const listenToOnlineUsers = (callback) => {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where("isOnline", "==", true));

    return onSnapshot(q, (snapshot) => {
        const users = [];
        snapshot.forEach((doc) => {
            users.push({ id: doc.id, ...doc.data() });
        });
        // Sort by recently active (robust)
        users.sort((a, b) => {
            const dateA = a.lastActive ? new Date(a.lastActive).getTime() : 0;
            const dateB = b.lastActive ? new Date(b.lastActive).getTime() : 0;
            return dateB - dateA; // Descending
        });
        callback(users);
    });
};


/**
 * Haversine formula to calculate distance in km
 */
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d;
};

const deg2rad = (deg) => {
    return deg * (Math.PI / 180);
};

/**
 * Subscribes a user to premium.
 * Simulates payment and updates Firestore.
 */
export const subscribeToPremium = async (uid) => {
    if (!uid) return null;
    const userRef = doc(db, 'users', uid);

    // Calculate expiry (30 days from now)
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 30);

    const updateData = {
        isPremium: true,
        subscriptionExpiry: expiryDate.toISOString()
    };

    try {
        await updateDoc(userRef, updateData);
        return updateData;
    } catch (error) {
        console.error("Error subscribing to premium:", error);
        throw error;
    }
};

/**
 * STREAMING & SIGNALING
 */

// Streaming logic moved to StreamService.js

// Streaming logic moved to StreamService.js

// Send a signal (offer/answer/candidate)
export const sendSignal = async (hostUid, payload) => {
    // payload: { type, from, to, data }
    const signalsRef = collection(db, 'activeStreams', hostUid, 'signals');
    await addDoc(signalsRef, {
        ...payload,
        createdAt: serverTimestamp()
    });
};

// Listen to signals directed at ME
export const listenToSignals = (hostUid, myUid, callback) => {
    const signalsRef = collection(db, 'activeStreams', hostUid, 'signals');
    // We listen for signals where 'to' == myUid
    // Composite index might be needed: collection(activeStreams/sid/signals) + where('to', '==', myUid)
    const q = query(signalsRef, where('to', '==', myUid));

    return onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
            if (change.type === 'added') {
                callback(change.doc.data(), change.doc.id);
                // Optionally delete signal after reading to keep it clean?
                deleteDoc(change.doc.ref);
            }
        });
    });
};

// Listen to all signals (Host Mode)
// Host needs to see requests from anyone ('to' == hostUid)
export const listenToHostSignals = (hostUid, callback) => {
    const signalsRef = collection(db, 'activeStreams', hostUid, 'signals');
    const q = query(signalsRef, where('to', '==', hostUid));

    return onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
            if (change.type === 'added') {
                callback(change.doc.data(), change.doc.id);
                deleteDoc(change.doc.ref);
            }
        });
    });
};

// Stream viewer count update
export const updateStreamViewers = async (hostUid) => {
    // Placeholder for viewer count logic
    console.log("updateStreamViewers called for", hostUid);
};

